import { useCallback, useEffect, useRef, useState } from 'react';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { DirTaskCategoryType } from '@econtractor/types';
import { useSettings } from 'context/SettingsContext';
import * as apiSettings from 'api/apiSettings';
import { getTaskCategoryUsage } from 'api/apiTask';
import { useToast } from 'context/ToastContext';
import ESpinner from 'components/others/Spinner';
import AddCategoryForm from './AddCategoryForm';
import CategoryList from './CategoryList';

interface OffTaskCategoriesProps {
    show: boolean;
    onHide: () => void;
}

interface CategoryItem {
    ID: number;
    name: string;
}

const extractItems = (settings: { DIRS?: { TaskCategory?: DirTaskCategoryType[] } } | null): CategoryItem[] => {
    return (settings?.DIRS?.TaskCategory || [])
        .filter((item) => !item.hidden && item.ID)
        .map((item) => ({ ID: item.ID, name: String(item.categoryName || '') }));
};

const OffTaskCategories = ({ show, onHide }: OffTaskCategoriesProps) => {
    const { t } = useTranslation(['Tasks', 'common']);
    const { showToast } = useToast();
    const { dispatchLocalSettings } = useSettings();
    const showToastRef = useRef(showToast);
    const tRef = useRef(t);
    showToastRef.current = showToast;
    tRef.current = t;

    const [items, setItems] = useState<CategoryItem[]>([]);
    const [usage, setUsage] = useState<Record<number, number>>({});
    const [loading, setLoading] = useState(false);

    const applyData = useCallback(
        (
            resSetting: NonNullable<Awaited<ReturnType<typeof apiSettings.getSettings>>>,
            resUsage: Record<number, number>,
        ) => {
            dispatchLocalSettings({ type: 'SET', settings: resSetting });
            setItems(extractItems(resSetting));
            setUsage(resUsage);
        },
        [dispatchLocalSettings],
    );

    const fetchData = useCallback(async () => {
        const [resSetting, resUsage] = await Promise.all([apiSettings.getSettings(), getTaskCategoryUsage()]);
        if (!resSetting || resUsage === null) return null;
        return { resSetting, resUsage };
    }, []);

    const syncData = useCallback(async (): Promise<void> => {
        const data = await fetchData();
        if (!data) {
            showToastRef.current(tRef.current('common:messError'), 'danger');
            return;
        }
        applyData(data.resSetting, data.resUsage);
    }, [applyData, fetchData]);

    useEffect(() => {
        if (!show) return;
        let cancelled = false;

        (async () => {
            setLoading(true);
            const data = await fetchData();
            if (cancelled) return;
            setLoading(false);
            if (!data) {
                showToastRef.current(tRef.current('common:messError'), 'danger');
                return;
            }
            applyData(data.resSetting, data.resUsage);
        })();

        return () => {
            cancelled = true;
        };
    }, [show, applyData, fetchData]);

    return (
        <Offcanvas show={show} onHide={onHide} placement="end">
            <Offcanvas.Header className="border-bottom" closeButton>
                <Offcanvas.Title>{t('dirs.categoriesTitle')}</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="position-relative pb-0 d-flex flex-column px-0">
                <div className="off-coming-form d-flex flex-column h-100">
                    {show ? <AddCategoryForm onCreated={syncData} /> : null}
                    <div className="off-coming-content">
                        {loading ? <ESpinner /> : show ? <CategoryList items={items} usage={usage} onChanged={syncData} /> : null}
                    </div>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default OffTaskCategories;
